/**
 * Deferred balance — what a member still owes on a booking the club deferred.
 *
 * An admin can book now and collect later: the booking stays `pending` with nothing
 * collected, and its fees sit on an open (`due`) invoice until the club takes the money at
 * the counter. Every booking-detail surface reads the booking's own `paymentAmount`, which
 * stays 0 until then — so without this, a member who owes money is shown "$0.00".
 *
 * Only that case yields a balance. Not a bank debit still settling (its invoice is
 * `processing`), not a debit the bank sent back (it carries a `returned` tender, and the
 * booking is `returned` on its own — or `pending` only because another payer's debit is in
 * flight), and not a comp (no invoice at all).
 *
 * Pure, so both member apps share the one decision and it is testable in node.
 *
 * @module butterUI/domains/billing/deriveDeferredBalance
 */

import { isActionableInvoiceStatus } from '@lgc/database/types/domains/payment'
import type { InvoiceStatus, InvoiceTotals } from '@lgc/database/types/domains/payment'

/**
 * The invoice fields this reads. `payments` is loosely typed on purpose: the member apps
 * each declare their own invoice shape, and mobile's types the tender list as `unknown[]`.
 */
export interface DeferredBalanceInvoice {
  status: InvoiceStatus
  totals: InvoiceTotals
  payments?: readonly unknown[]
}

export interface DeferredBalance {
  /** What is still owed, dollars — the invoice's own `amountDue`, tax and any fee included. */
  amountDue: number
  /**
   * The booking's charges with tax, before the processing fee and its adjustment — the
   * figure for the booking's own row. The fee rows sit under it, so the rows add up to
   * `amountDue` at a club that taxes.
   */
  charges: number
}

export function deriveDeferredBalance(
  booking: { paymentStatus?: string | null } | null | undefined,
  invoice: DeferredBalanceInvoice | null | undefined,
): DeferredBalance | null {
  if (!booking || !invoice) return null
  if (booking.paymentStatus !== 'pending') return null
  if (!isActionableInvoiceStatus(invoice.status)) return null
  if (invoice.payments?.some(isReturnedTender)) return null

  const { totals } = invoice
  return {
    amountDue: totals.amountDue,
    // `?? 0`: an older API may predate the adjustment field (see deriveInvoiceTotalsRows).
    charges: round2(totals.total - totals.processingFee - (totals.adjustmentTotal ?? 0)),
  }
}

const round2 = (value: number): number => Math.round(value * 100) / 100

const isReturnedTender = (payment: unknown): boolean =>
  typeof payment === 'object' && payment !== null && (payment as { status?: unknown }).status === 'returned'
